import { useCallback, useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/api";

const TARGET_SR = 44100;

const toInt16 = (input, srcRate, gain) => {
  const ratio = srcRate / TARGET_SR;
  const len = Math.floor(input.length / ratio);
  const out = new Int16Array(len);
  for (let i = 0; i < len; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const v = (input[i0] + (input[i1] - input[i0]) * (pos - i0)) * gain;
    out[i] = Math.max(-1, Math.min(1, v)) * 0x7fff;
  }
  return out;
};

export function useMic(getToken) {
  const [state, setState] = useState("off"); // off | connecting | live
  const [level, setLevel] = useState(0);
  const [muted, setMuted] = useState(false);
  const [gain, setGain] = useState(1);
  const gainRef = useRef(1);
  const refs = useRef({});

  useEffect(() => { gainRef.current = gain; }, [gain]);

  const stop = useCallback(() => {
    const r = refs.current;
    r.proc?.disconnect();
    r.src?.disconnect();
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close().catch(() => {});
    if (r.ws && r.ws.readyState <= 1) r.ws.close();
    refs.current = {};
    setState("off");
    setLevel(0);
    setMuted(false);
  }, []);

  const start = useCallback(async () => {
    setState("connecting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      const token = await getToken();
      const ws = new WebSocket(`${BACKEND_URL.replace(/^http/, "ws")}/api/studio/mic?token=${encodeURIComponent(token)}`);
      ws.binaryType = "arraybuffer";
      let ctx;
      try { ctx = new AudioContext({ sampleRate: TARGET_SR }); } catch { ctx = new AudioContext(); }
      const src = ctx.createMediaStreamSource(stream);
      const proc = ctx.createScriptProcessor(2048, 1, 1);
      const silent = ctx.createGain();
      silent.gain.value = 0;
      src.connect(proc);
      proc.connect(silent);
      silent.connect(ctx.destination);
      proc.onaudioprocess = (e) => {
        const data = e.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
        setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4 * gainRef.current));
        if (ws.readyState === 1) ws.send(toInt16(data, ctx.sampleRate, gainRef.current).buffer);
      };
      refs.current = { stream, ws, ctx, src, proc };
      ws.onopen = () => setState("live");
      ws.onclose = () => { if (refs.current.ws === ws) stop(); };
    } catch (e) {
      stop();
      throw e;
    }
  }, [getToken, stop]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    refs.current.ws?.send(next ? "mute" : "unmute");
  }, [muted]);

  useEffect(() => stop, [stop]);

  return { state, level: muted ? 0 : level, muted, gain, setGain, start, stop, toggleMute };
}
